import { useEffect, useMemo, useState } from 'react';
import { ExpiryStatus } from '../../../../../Generated/Commodity-Management-Api';
import { GroupingOption } from '../../../../../Types';
import { StoneXDivider, StoneXRow } from '../../../../StoneX';
import { StoneXAutocomplete, StoneXButton, StoneXCheckbox, StoneXSelect, StoneXTextField } from '../../../../StoneXMui';
import { SummaryReportConfiguration } from '../../Reports/Reports/Summary/Types';
import { PositionLikeReportSettingsPageProps } from './types';

function parseSummaryReportConfiguration(configurationJson: string): SummaryReportConfiguration {
  return JSON.parse(configurationJson) as SummaryReportConfiguration;
}

export default function SummaryReportSettingsPage({ report, groupingOptions, isSaving, onDeleteReport, onUpdateReport }: PositionLikeReportSettingsPageProps) {
  const initialConfiguration = useMemo(() => parseSummaryReportConfiguration(report.configuration), [report.configuration]);

  const [configuration, setConfiguration] = useState<SummaryReportConfiguration>(initialConfiguration);
  const [reportName, setReportName] = useState<string>(report.reportName);

  const [showQuoteDate, setShowQuoteDate] = useState<boolean>(initialConfiguration.inputs?.quoteDate?.show ?? true);
  const [showBudgetStartDate, setShowBudgetStartDate] = useState<boolean>(initialConfiguration.inputs?.budgetStartDate?.show ?? true);
  const [showBudgetEndDate, setShowBudgetEndDate] = useState<boolean>(initialConfiguration.inputs?.budgetEndDate?.show ?? true);

  const [level1GroupingOption, setLevel1GroupingOption] = useState<string | null>(initialConfiguration.behavior?.grouping?.[0] ?? 'Commodity');
  const [level2GroupingOption, setLevel2GroupingOption] = useState<string | null>(initialConfiguration.behavior?.grouping?.[1] ?? null);
  const [expiryStatus, setExpiryStatus] = useState<ExpiryStatus>(initialConfiguration.behavior?.expiryStatus ?? ExpiryStatus.Open);
  const [netOptionPremiums, setNetOptionPremiums] = useState<boolean>(initialConfiguration.behavior?.netOptionPremiums ?? false);

  useEffect(() => {
    const parsedConfiguration = parseSummaryReportConfiguration(report.configuration);

    setConfiguration(parsedConfiguration);
    setReportName(report.reportName);
    setShowQuoteDate(parsedConfiguration.inputs?.quoteDate?.show ?? true);
    setShowBudgetStartDate(parsedConfiguration.inputs?.budgetStartDate?.show ?? true);
    setShowBudgetEndDate(parsedConfiguration.inputs?.budgetEndDate?.show ?? true);
    setLevel1GroupingOption(parsedConfiguration.behavior?.grouping?.[0] ?? 'Commodity');
    setLevel2GroupingOption(parsedConfiguration.behavior?.grouping?.[1] ?? null);
    setExpiryStatus(parsedConfiguration.behavior?.expiryStatus ?? ExpiryStatus.Open);
    setNetOptionPremiums(parsedConfiguration.behavior?.netOptionPremiums ?? false);
  }, [report]);

  function resetConfiguration() {
    const parsedConfiguration = parseSummaryReportConfiguration(report.configuration);

    setConfiguration(parsedConfiguration);
    setReportName(report.reportName);
    setShowQuoteDate(parsedConfiguration.inputs?.quoteDate?.show ?? true);
    setShowBudgetStartDate(parsedConfiguration.inputs?.budgetStartDate?.show ?? true);
    setShowBudgetEndDate(parsedConfiguration.inputs?.budgetEndDate?.show ?? true);
    setLevel1GroupingOption(parsedConfiguration.behavior?.grouping?.[0] ?? 'Commodity');
    setLevel2GroupingOption(parsedConfiguration.behavior?.grouping?.[1] ?? null);
    setExpiryStatus(parsedConfiguration.behavior?.expiryStatus ?? ExpiryStatus.Open);
    setNetOptionPremiums(parsedConfiguration.behavior?.netOptionPremiums ?? false);
  }

  function saveConfiguration() {
    const grouping = [level1GroupingOption, level2GroupingOption].filter((x) => x !== null) as GroupingOption[];

    const nextConfiguration: SummaryReportConfiguration = {
      ...configuration,
      inputs: {
        quoteDate: { show: showQuoteDate },
        budgetStartDate: { show: showBudgetStartDate },
        budgetEndDate: { show: showBudgetEndDate },
      },
      behavior: {
        ...configuration.behavior,
        expiryStatus,
        netOptionPremiums,
        grouping,
      },
      columns: {
        available: configuration.columns?.available ?? [],
        active: configuration.columns?.active ?? [],
      },
    };

    onUpdateReport({
      ...report,
      reportName: reportName.trim(),
      configuration: JSON.stringify(nextConfiguration),
    });

    setConfiguration(nextConfiguration);
  }

  return (
    <div>
      <StoneXRow>
        <StoneXTextField value={reportName} label="Report Name" onUpdate={setReportName} />
      </StoneXRow>

      <StoneXDivider />

      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 380px' }}>
          <h3>Inputs</h3>
          <StoneXRow>
            <StoneXCheckbox
              label="Show Quote Date"
              helperText="Show quote date selector"
              checked={showQuoteDate}
              onUpdate={setShowQuoteDate}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXCheckbox
              label="Show Budget Start Date"
              helperText="Show start budget date selector"
              checked={showBudgetStartDate}
              onUpdate={setShowBudgetStartDate}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXCheckbox
              label="Show Budget End Date"
              helperText="Show end budget date selector"
              checked={showBudgetEndDate}
              onUpdate={setShowBudgetEndDate}
            />
          </StoneXRow>
        </div>

        <div style={{ flex: '1 1 360px' }}>
          <h3>Behavior</h3>
          <StoneXRow>
            <StoneXAutocomplete
              label="Group By"
              helperText="The first grouping level"
              options={groupingOptions}
              value={level1GroupingOption}
              onUpdate={(value) => setLevel1GroupingOption(value)}
              getOptionLabel={(x) => x}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXAutocomplete
              label="Then By (Optional)"
              helperText="The second grouping level"
              options={groupingOptions}
              value={level2GroupingOption}
              onUpdate={(value) => setLevel2GroupingOption(value)}
              getOptionLabel={(x) => x}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXSelect
              label="Months to show"
              helperText="Whether to include expired months, open months, or all months"
              options={[ExpiryStatus.All, ExpiryStatus.Expired, ExpiryStatus.Open]}
              getId={(x) => x}
              getOptionLabel={(x) => x}
              value={expiryStatus}
              onChange={(value) => setExpiryStatus(value ?? ExpiryStatus.Open)}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXCheckbox
              label="Net Option Premiums"
              helperText="Include option premium costs in summary calculations"
              checked={netOptionPremiums}
              onUpdate={setNetOptionPremiums}
            />
          </StoneXRow>
        </div>
      </div>

      <StoneXRow justify="end" marginDirection="top">
        <StoneXButton variant="outlined" onClick={() => onDeleteReport(report.customerReportId)}>Delete Report</StoneXButton>
        <StoneXButton variant="outlined" onClick={resetConfiguration}>Reset</StoneXButton>
        <StoneXButton isWorking={isSaving} onClick={saveConfiguration}>Save</StoneXButton>
      </StoneXRow>
    </div>
  );
}
